import { ApolloClient, useApolloClient, useMutation } from "@apollo/client";
import Check from "@mui/icons-material/Check";
import Close from "@mui/icons-material/Close";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Merge from "@mui/icons-material/Merge";
import { Button, ListItemIcon, ListItemText, Menu, MenuItem, MenuList } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { gql } from "generated-graphql";
import { Permission, TenantCatalogChemicalFragment } from "generated-graphql/graphql";
import { useAuthorization } from "hooks/useAuthorization";
import pluralize from "pluralize";
import { useState } from "react";

import { ChemicalMergePicker } from "./ChemicalMergePicker";

const BULK_VERIFY_CHEMICALS_MUTATION = gql(`
    mutation BulkVerifyChemicals($chemicalIds: [ID!]!) {
        verifyChemicals(chemicalIds: $chemicalIds)
    }
`);

const BULK_UNVERIFY_CHEMICALS_MUTATION = gql(`
     mutation BulkUnverifyChemicals($chemicalIds: [ID!]!) {
        unverifyChemicals(chemicalIds: $chemicalIds)
    }`);

// Optimistically update cache: set needsReview to false for verified chemicals
function markChemicalsVerifiedInCache(client: ApolloClient<object>, chemicalIds: string[]) {
  for (const chemicalId of chemicalIds) {
    client.cache.modify({
      id: client.cache.identify({ __typename: "Chemical", id: chemicalId }),
      fields: {
        needsReview: () => false,
        verifiedAt: () => new Date().toISOString(),
      },
    });
  }
}

// Update cache: clear verification fields
function markChemicalsUnverifiedInCache(client: ApolloClient<object>, chemicalIds: string[]) {
  for (const chemicalId of chemicalIds) {
    client.cache.modify({
      id: client.cache.identify({ __typename: "Chemical", id: chemicalId }),
      fields: {
        needsReview: () => true,
        verifiedAt: () => null,
        verifiedBy: () => null,
      },
    });
  }
}

export const ChemicalCatalogBulkActions = function ({
  chemicals,
}: {
  chemicals: TenantCatalogChemicalFragment[];
}) {
  const buttonId = "chemical-catalog-bulk-actions";
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openDialog, setDialogOpen] = useState<undefined | "verify" | "unverify">(undefined);
  const [mergeOpen, setMergeOpen] = useState(false);
  const alerts = useAlerts();
  const client = useApolloClient();
  const { hasPermissions } = useAuthorization();
  const canMerge = hasPermissions([Permission.MergeChemicals]);
  const chemicalIds = chemicals.map((chemical) => chemical.id);

  const [bulkVerifyChemicals, { loading: bulkVerifyingChemicals }] = useMutation(
    BULK_VERIFY_CHEMICALS_MUTATION
  );

  const [bulkUnverifyChemicals, { loading: bulkUnverifyingChemicals }] = useMutation(
    BULK_UNVERIFY_CHEMICALS_MUTATION
  );

  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Button
        id={buttonId}
        aria-controls={open ? "basic-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        variant="contained"
        onClick={handleClick}
        endIcon={<ExpandMore />}
        size="medium"
        disabled={chemicals.length === 0}
      >
        Bulk Actions
      </Button>
      <Menu
        id="add-chemical-actions-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": buttonId,
        }}
      >
        <MenuList dense>
          <MenuItem
            key="bulk-verify"
            onClick={() => {
              setDialogOpen("verify");
              handleClose();
            }}
            disabled={chemicals.length === 0}
          >
            <ListItemIcon>
              <Check />
            </ListItemIcon>
            <ListItemText>Verify Chemicals</ListItemText>
          </MenuItem>
          <MenuItem
            key="bulk-unverify"
            onClick={() => {
              setDialogOpen("unverify");
              handleClose();
            }}
            disabled={chemicals.length === 0}
          >
            <ListItemIcon>
              <Close />
            </ListItemIcon>
            <ListItemText>Unverify Chemicals</ListItemText>
          </MenuItem>
          {canMerge && (
            <MenuItem
              key="merge"
              disabled={chemicalIds.length < 2}
              onClick={() => {
                setMergeOpen(true);
                handleClose();
              }}
            >
              <ListItemIcon>
                <Merge />
              </ListItemIcon>
              <ListItemText>Merge Chemicals</ListItemText>
            </MenuItem>
          )}
        </MenuList>
      </Menu>
      {mergeOpen && (
        <ChemicalMergePicker open onClose={() => setMergeOpen(false)} chemicalIds={chemicalIds} />
      )}
      <ConfirmDialog
        open={!!openDialog}
        onClose={() => setDialogOpen(undefined)}
        loading={bulkVerifyingChemicals || bulkUnverifyingChemicals}
        onConfirm={async () => {
          try {
            if (openDialog === "verify") {
              await bulkVerifyChemicals({
                variables: { chemicalIds },
              });
              markChemicalsVerifiedInCache(client, chemicalIds);
            } else {
              await bulkUnverifyChemicals({
                variables: { chemicalIds },
              });
              markChemicalsUnverifiedInCache(client, chemicalIds);
            }
            alerts.success(
              `Successfully ${openDialog === "verify" ? "verified" : "unverified"} chemicals.`
            );
          } catch (err) {
            alerts.error(
              `There was an error ${
                openDialog === "verify" ? "verifying" : "unverifying"
              } chemicals`,
              { err }
            );
          }
          setDialogOpen(undefined);
        }}
        header={`Are you sure you want to proceed to ${openDialog}?`}
        confirmText={`Yes, ${openDialog}`}
        info={`Are you sure you want to ${openDialog} the selected ${chemicals.length} ${pluralize(
          "chemical",
          chemicals.length
        )}? This indicates that these chemicals in Encamp ${
          openDialog === "verify" ? "" : "do not"
        } match their SDS documents. Other users of your organization will see these chemicals as ${
          openDialog === "verify" ? "verified" : "unverified"
        }.`}
      />
    </>
  );
};
